"use client";

import * as Sentry from "@sentry/nextjs";
import { getFileUploadToken } from "api/getFileUploadToken";
import ky, { type Progress } from "ky";
import { getKyErrorDetails } from "./helpers/getKyErrorDetails";

const UPLOAD_TIMEOUT_MS = 20 * 60 * 1000;

export interface UploadFileResponse {
    fileid: string;
}

export interface UploadFileResult {
    isSuccess: boolean;
    data: UploadFileResponse | null;
    error: Error | null;
}

export interface UploadFileClientOptions {
    onUploadProgress?: (progress: Progress, chunk: Uint8Array) => void;
}

export async function uploadFileClient(file: File, options?: UploadFileClientOptions): Promise<UploadFileResult> {
    try {
        const tokenData = await getFileUploadToken();

        const formData = new FormData();
        formData.append("file", file);

        const apiUrl = process.env.NEXT_PUBLIC_API_FORMS_URL;
        if (!apiUrl) {
            throw new Error("NEXT_PUBLIC_API_FORMS_URL is not defined");
        }

        const response = await ky
            .post(`${apiUrl}/forms/files`, {
                body: formData,
                headers: {
                    Authorization: `${tokenData.token_type} ${tokenData.access_token}`,
                },
                timeout: UPLOAD_TIMEOUT_MS,
                ...(options?.onUploadProgress ? { onUploadProgress: options.onUploadProgress } : {}),
            })
            .json<UploadFileResponse>();

        return {
            isSuccess: true,
            data: response,
            error: null,
        };
    } catch (error) {
        console.error("File upload failed:", error);
        const errorDetails = await getKyErrorDetails(error);
        Sentry.withScope((scope) => {
            scope.setTag("feature", "client-file-upload");
            scope.setExtras({
                fileName: file.name,
                fileSize: file.size,
                fileType: file.type,
                errorDetails,
            });
            Sentry.captureException(error);
        });

        return {
            isSuccess: false,
            data: null,
            error: error instanceof Error ? error : new Error("Unknown error during file upload"),
        };
    }
}
